import React from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userLabel: string;
  /** undefined means default-tier mode (no explicit tier). */
  tier?: App.LuxLoyaltyTier;
  isSubmitting: boolean;
}

/**
 * Confirmation dialog shown before the admin commits an enrol request. The
 * caller owns the mutation; this component only renders the prompt.
 */
export default function LuxLoyaltyEnrolConfirmDialog({
  open,
  onClose,
  onConfirm,
  userLabel,
  tier,
  isSubmitting,
}: Props) {
  const tierLabel = tier ? getLuxLoyaltyTierDisplayName(tier) : 'Default tier';

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm Société enrolment</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography component="span">
            Enrol <strong>{userLabel}</strong> into Société as <strong>{tierLabel}</strong>?
          </Typography>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary">
              {tier
                ? 'The customer will be enrolled directly at this tier with no further checks.'
                : 'Default uses Société’s standard enrolment rules (currently Bronze unless a pre-handler resolves a different tier).'}
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              This action takes effect immediately and cannot be undone from the admin portal.
            </Typography>
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Enrolling…' : 'Confirm enrolment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
